import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, Clock, Users, Play, Award } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const isLocalFavourite = (exp) => exp.rating >= 4.5 && exp.review_count >= 5;

const ExperienceCard = ({ experience, compact = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const { formatPrice } = useCurrency();

  // Handle missing images
  const images = experience.images?.length > 0 
    ? experience.images 
    : ['https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600'];

  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (compact) {
    return (
      <Link to={`/experience/${experience.id}`} className="group flex gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100" data-testid={`experience-card-compact-${experience.id}`}>
        <div className="relative w-24 h-24 flex-shrink-0 overflow-hidden rounded-lg">
          <img
            src={images[0]}
            alt={experience.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600'; }}
          />
          <div className="absolute top-1 left-1 bg-[#5C7A3A] text-white px-1.5 py-0.5 rounded text-[10px] font-medium">
            {experience.category}
          </div>
        </div>
        <div className="flex-1 min-w-0 py-0.5">
          <div className="flex justify-between items-start gap-1">
            <p className="text-xs text-gray-500 truncate">{experience.location}</p>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Star className="w-3 h-3 fill-gray-900" />
              <span className="text-xs">{experience.rating?.toFixed(1) || 'New'}</span>
            </div>
          </div>
          <h3 className="font-medium text-sm text-gray-900 truncate mt-0.5">{experience.title}</h3>
          {isLocalFavourite(experience) && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#5C7A3A] mt-0.5" data-testid="local-favourite-badge-compact">
              <Award className="w-3 h-3" /> Local Favourite
            </span>
          )}
          <div className="flex items-center gap-2 text-gray-500 text-xs mt-1">
            <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{experience.duration}</span>
            <span className="flex items-center gap-0.5"><Users className="w-3 h-3" />{experience.max_guests}</span>
          </div>
          <p className="mt-1 text-sm">
            <span className="font-semibold">{formatPrice(experience.price)}</span>
            <span className="text-gray-500 text-xs"> / person</span>
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/experience/${experience.id}`} className="group block" data-testid={`experience-card-${experience.id}`}>
      <div
        className="relative aspect-square overflow-hidden rounded-xl"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image */}
        <img
          src={images[currentImageIndex]}
          alt={experience.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600';
          }}
        />

        {/* Video indicator */}
        {experience.video && (
          <div className="absolute bottom-3 left-3 bg-black/70 text-white px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1">
            <Play className="w-3 h-3 fill-white" />
            Video
          </div>
        )}

        {/* Category Badge */}
        <div className="absolute top-3 left-3 bg-[#5C7A3A] text-white px-2 py-1 rounded-md text-xs font-medium shadow-md">
          {experience.category}
        </div>

        {/* Navigation Arrows */}
        {isHovered && images.length > 1 && (
          <>
            {currentImageIndex > 0 && (
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1.5 shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            {currentImageIndex < images.length - 1 && (
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-1.5 shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:scale-105"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </>
        )}

        {/* Dots Indicator */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.slice(0, 5).map((_, index) => (
              <div
                key={`dot-${index}`}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  currentImageIndex === index
                    ? 'bg-white w-2'
                    : 'bg-white/60'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Experience Info */}
      <div className="mt-3">
        <div className="flex justify-between items-start gap-2">
          <h3 className="font-medium text-gray-900 truncate">{experience.location}</h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Star className="w-4 h-4 fill-gray-900" />
            <span className="text-sm">{experience.rating?.toFixed(1) || 'New'}</span>
          </div>
        </div>
        <p className="text-gray-500 text-sm truncate">{experience.title}</p>
        {isLocalFavourite(experience) && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#5C7A3A] mt-1" data-testid="local-favourite-badge">
            <Award className="w-3.5 h-3.5" /> Local Favourite
          </span>
        )}
        <div className="flex items-center gap-3 text-gray-500 text-sm mt-1">
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {experience.duration}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            Up to {experience.max_guests}
          </span>
        </div>
        <p className="mt-1">
          <span className="font-semibold">{formatPrice(experience.price)}</span>
          <span className="text-gray-500"> / person</span>
        </p>
      </div>
    </Link>
  );
};

export default ExperienceCard;
